import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import {Loader, fetchWithLoader, processResponse} from "../components/Loader";
import AnimatedButton from "../components/AnimatedButton";
import {PageContext} from "../PageContext";

export type UsageStatus = "COMPLETED" | "ERROR" | "IN_PROGRESS" | "AWAITING_CONFIRM"

export interface UsageSummary {
    balance: number
    creditsUsed30d: number
    requests30d: number
    avgCredits: number
}

export interface UsageRow {
    id: string
    date: string
    model: string
    ide?: string | null
    inputTokens?: number | null
    outputTokens?: number | null
    estimatedCredits?: number | null
    chargedCredits?: number | null
    status: UsageStatus
    flat?: boolean
}

export interface UsageResponse {
    summary: UsageSummary
    rows: UsageRow[]
    nextCursor: string | null
}

const STATUS_LABELS: Record<UsageStatus, { label: string, color: string, title?: string }> = {
    COMPLETED: {label: "Completed", color: "#2e7d32"},
    ERROR: {label: "Failed", color: "#c62828", title: "Failed requests may still be charged for AI work already done."},
    AWAITING_CONFIRM: {label: "Plan, awaiting confirm", color: "#ef6c00"},
    IN_PROGRESS: {label: "In progress", color: "#1565c0"},
}

function formatK(n?: number | null): string {
    if (n === null || n === undefined) return "—"
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k`
    return `${n}`
}

function formatNum(n?: number | null): string {
    if (n === null || n === undefined) return "—"
    return n.toLocaleString()
}

const cellStyle: React.CSSProperties = {padding: "8px 12px", textAlign: "left", whiteSpace: "nowrap"}

export default function UsagePage() {
    const context = useContext(PageContext)
    const contextRef = useRef(context)
    const location = useLocation()
    const [isTestMode, setIsTestMode] = useState<boolean>(false)
    const [summary, setSummary] = useState<UsageSummary | null>(null)
    const [rows, setRows] = useState<UsageRow[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const [hasError, setHasError] = useState<boolean>(false)
    const isAdmin = context.loggedIn && context.admin
    const prefix = isTestMode ? "test/" : ""

    useEffect(() => {
        contextRef.current = context
    }, [context])

    const load = useCallback((cursor: string | null) => {
        const append = cursor !== null
        if (append) setLoadingMore(true); else setLoading(true)
        const url = `/api/v1/${prefix}usage` + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : "")
        fetchWithLoader(() => {
            if (append) setLoadingMore(false); else setLoading(false)
            contextRef.current.onLoadComplete()
        }, url).then(async (response) => {
            await processResponse(response, (json) => {
                const data = json as UsageResponse
                setSummary(data.summary)
                setRows(prev => append ? [...prev, ...data.rows] : data.rows)
                setNextCursor(data.nextCursor ?? null)
                setHasError(false)
            }, (errorKey, errorMessage) => {
                // Logged-out users are redirected to log in by the shared error handler.
                contextRef.current.handleError(errorKey, location.pathname + location.search, errorMessage)
                setHasError(true)
            })
        })
    }, [prefix, location])

    useEffect(() => {
        load(null)
    }, [load])

    return (
        <>
            <Helmet>
                <title>Usage — Codebuddy</title>
                <meta name="robots" content="noindex"/>
            </Helmet>
            <div className={"gradient__07_smooth full-page-gradient"}>
                {
                    isAdmin && (
                        <div style={{position: "absolute", right: "100px", top: "100px"}}>
                            <AnimatedButton onClick={() => setIsTestMode(!isTestMode)}>
                                {isTestMode ? "Test Mode" : "Live Mode"}
                            </AnimatedButton>
                        </div>
                    )
                }
                <div className="section">
                    <div className="container">
                        <h1>Usage</h1>
                        <p className="text-lg">Every request you make, what it was estimated at, and what it was charged.</p>
                        <Loader loading={loading || hasError}>
                            {summary && (
                                <>
                                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", margin: "24px 0"}}>
                                        <SummaryTile label="Credits available" value={formatNum(summary.balance)}/>
                                        <SummaryTile label="Credits used (last 30 days)" value={formatNum(summary.creditsUsed30d)}/>
                                        <SummaryTile label="Requests (last 30 days)" value={formatNum(summary.requests30d)}/>
                                        <SummaryTile label="Average credits per request" value={formatNum(summary.avgCredits)}/>
                                    </div>
                                    <div style={{overflowX: "auto"}}>
                                        <table style={{width: "100%", borderCollapse: "collapse"}}>
                                            <thead>
                                            <tr style={{borderBottom: "1px solid rgba(255,255,255,0.2)"}}>
                                                <th style={cellStyle}>When</th>
                                                <th style={cellStyle}>Model</th>
                                                <th style={cellStyle}>Where</th>
                                                <th style={cellStyle}>Context in</th>
                                                <th style={cellStyle}>Out</th>
                                                <th style={cellStyle}>Estimate</th>
                                                <th style={cellStyle}>Charged</th>
                                                <th style={cellStyle}>Status</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {rows.length === 0 && (
                                                <tr>
                                                    <td style={cellStyle} colSpan={8}>No requests yet.</td>
                                                </tr>
                                            )}
                                            {rows.map(row => {
                                                const status = STATUS_LABELS[row.status] ?? {label: row.status, color: "#666"}
                                                return (
                                                    <tr key={row.id} style={{borderBottom: "1px solid rgba(255,255,255,0.08)"}}>
                                                        <td style={cellStyle}>{new Date(row.date).toLocaleString()}</td>
                                                        <td style={cellStyle}>{row.model}</td>
                                                        <td style={cellStyle}>{row.ide ?? "—"}</td>
                                                        <td style={cellStyle}>{formatK(row.inputTokens)}</td>
                                                        <td style={cellStyle}>{formatK(row.outputTokens)}</td>
                                                        <td style={cellStyle}>{formatNum(row.estimatedCredits)}</td>
                                                        <td style={cellStyle}>
                                                            <b>{formatNum(row.chargedCredits)}</b>
                                                            {row.flat && (
                                                                <span style={{opacity: 0.6, marginLeft: "6px"}}>(flat price)</span>
                                                            )}
                                                        </td>
                                                        <td style={cellStyle}>
                                                            <span title={status.title} style={{
                                                                display: "inline-block",
                                                                padding: "2px 10px",
                                                                borderRadius: "999px",
                                                                fontSize: "0.85em",
                                                                color: "#fff",
                                                                background: status.color,
                                                            }}>{status.label}</span>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {nextCursor && (
                                        <div style={{marginTop: "16px"}}>
                                            <Loader loading={loadingMore} size="sm">
                                                <AnimatedButton onClick={() => load(nextCursor)}>Load more</AnimatedButton>
                                            </Loader>
                                        </div>
                                    )}
                                    <p style={{opacity: 0.7, marginTop: "24px", fontSize: "0.9em"}}>
                                        Charged for the AI work used, rounded up to whole credits. Includes every
                                        AI call the request made. Requests made with your own API key show 0.
                                        Failed requests are charged only for AI work already done.
                                    </p>
                                </>
                            )}
                        </Loader>
                    </div>
                </div>
            </div>
        </>
    )
}

function SummaryTile(props: { label: string, value: string }) {
    return (
        <div style={{padding: "16px", borderRadius: "12px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)"}}>
            <div style={{opacity: 0.7, fontSize: "0.9em"}}>{props.label}</div>
            <div style={{fontSize: "1.8em", fontWeight: 700}}>{props.value}</div>
        </div>
    )
}
